import { useEffect, useState } from 'react';
import { api, type Sede } from '../lib/api.js';
import { fmtBs, fmtInt, fmtUsd } from '../lib/format.js';
import type { Currency } from '../lib/filters.js';

interface Row {
  codigo: string;
  diamond: number;
  ocupadasSinCh: number;
  roomRevenueBs: number;
  roomRevenueUsd: number;
  totalNoches: number;
}

/**
 * Totalización mensual por hotel (PROMPT §2.4): suma de TODOS los días del año
 * (MTD acumulado) por sede — Diamond, Room Revenue y Rooms Occupied minus
 * Comp & House. Diamond viene del reporte Diamond; Room Revenue y ocupadas del
 * flash; todo vive en registro_diario y se suma vía /dashboard/monthly.
 */
export function MonthlyTotalsTable({
  anio,
  sedesParam,
  sedeList,
  currency,
}: {
  anio: number;
  sedesParam?: string;
  sedeList: Sede[];
  currency: Currency;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .monthly({ anio, sedes: sedesParam })
      .then((r) => {
        if (!alive) return;
        const bySede = new Map<number, Row>();
        for (const m of r.rollups) {
          const codigo = sedeList.find((s) => s.id === m.sedeId)?.codigo ?? `#${m.sedeId}`;
          const acc = bySede.get(m.sedeId) ?? {
            codigo,
            diamond: 0,
            ocupadasSinCh: 0,
            roomRevenueBs: 0,
            roomRevenueUsd: 0,
            totalNoches: 0,
          };
          acc.diamond += m.nochesDiamond;
          acc.ocupadasSinCh += m.ocupadasSinCh;
          acc.roomRevenueBs += m.roomRevenueBs;
          acc.roomRevenueUsd += m.roomRevenueUsd ?? 0;
          acc.totalNoches += m.totalNochesHotel;
          bySede.set(m.sedeId, acc);
        }
        setRows([...bySede.values()].sort((a, b) => b.roomRevenueUsd - a.roomRevenueUsd));
      })
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [anio, sedesParam, sedeList]);

  const isBs = currency === 'BS';
  const tot = rows.reduce(
    (t, r) => ({
      diamond: t.diamond + r.diamond,
      ocupadasSinCh: t.ocupadasSinCh + r.ocupadasSinCh,
      roomRevenueBs: t.roomRevenueBs + r.roomRevenueBs,
      roomRevenueUsd: t.roomRevenueUsd + r.roomRevenueUsd,
      totalNoches: t.totalNoches + r.totalNoches,
    }),
    { diamond: 0, ocupadasSinCh: 0, roomRevenueBs: 0, roomRevenueUsd: 0, totalNoches: 0 },
  );
  const rev = (r: { roomRevenueBs: number; roomRevenueUsd: number }) =>
    isBs ? fmtBs(r.roomRevenueBs) : fmtUsd(r.roomRevenueUsd);

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <h2 className="mb-3 text-sm font-medium text-slate-200">
        Totalización mensual por hotel — {anio} (acumulado del año)
      </h2>
      {loading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">Sin datos para el año.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="px-2 py-1">Sede</th>
                <th className="px-2 py-1 text-right">Diamond</th>
                <th className="px-2 py-1 text-right">Ocupadas (sin C/H)</th>
                <th className="px-2 py-1 text-right">Room Revenue ({isBs ? 'Bs' : 'USD'})</th>
                <th className="px-2 py-1 text-right">Total noches</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.codigo} className="border-t border-slate-800 text-slate-300">
                  <td className="px-2 py-1.5 font-mono text-blue-400">{r.codigo}</td>
                  <td className="px-2 py-1.5 text-right">{fmtInt(r.diamond)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtInt(r.ocupadasSinCh)}</td>
                  <td className="px-2 py-1.5 text-right">{rev(r)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtInt(r.totalNoches)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-600 font-semibold text-slate-100">
                <td className="px-2 py-1.5">TOTAL</td>
                <td className="px-2 py-1.5 text-right">{fmtInt(tot.diamond)}</td>
                <td className="px-2 py-1.5 text-right">{fmtInt(tot.ocupadasSinCh)}</td>
                <td className="px-2 py-1.5 text-right">{rev(tot)}</td>
                <td className="px-2 py-1.5 text-right">{fmtInt(tot.totalNoches)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
